class DiscountCampaignDialog extends HTMLDialogElement {
  constructor() {
    super()

    this.strings = window.discountCampaign.strings.dialog

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]')) {
        this.close()
      }

      if (event.target.closest('[data-submit]')) {
        window.location.href = '/checkout'
      }
    })

    this.innerHTML = `
      <button class="dialog-close" data-close>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="5" y1="5" x2="19" y2="19" stroke="black" stroke-width="2" stroke-linecap="round"/>
          <line x1="19" y1="5" x2="5" y2="19" stroke="black" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <div>
        <h2>${this.strings.title}</h2>
        <p>${this.strings.text}</p>
        <p class="dialog-warning">${this.strings.warning}</p>

        <div class="flex flex-col">
          <button class="btn btn--primary mb-4" data-close>${this.strings.continue}</button>
          <button class="btn btn--secondary" data-submit>${this.strings.submit}</button>
        </div>
      </div>
    `
  }
}

customElements.define('discount-campaign-dialog', DiscountCampaignDialog, { extends: 'dialog' })

class DiscountCampaignManager {
  constructor() {
    if (DiscountCampaignManager.instance) return DiscountCampaignManager.instance

    this.state = window.discountCampaign
    this.conditions = {
      minimumQuantity: this.state.minPurchaseQuantity ?? 0,
      minPurchaseValue: parseInt(this.state.minPurchaseValue, 10) * 100 || 0,
    }
    this.listeners = []
    this.cart = null
    this.fetchCart()

    this.subscribeToCartChanges()

    this.dialog = document.createElement('dialog', { is: 'discount-campaign-dialog' })
    this.dialog.classList.add('discount-campaign-dialog')
    document.body.appendChild(this.dialog)

    DiscountCampaignManager.instance = this
  }

  subscribeToCartChanges() {
    document.addEventListener('on:cart:add', (event) => {
      if (event.detail?.cart) {
        this.updateCart(event.detail.cart)
      }
    })

    document.addEventListener('on:line-item:change', (event) => {
      if (event.detail?.cart) {
        this.updateCart(event.detail.cart)
      }
    })
  }

  async updateCart(cart, showModal = true) {
    this.cart = cart

    if (!this.conditionsMet && showModal) {
      if (window.location.pathname === '/cart') {
        this.dialog.showModal()
      }
    } else {
      // if is cart page, reload the page to make sure everything is updated
      if (window.location.pathname === '/cart' && this.isJustSwitched) {
        window.location.reload()
      }
    }

    this.cartUpdated()
  }

  get conditionsMet() {
    let valid = true
    if (this.cart.total_price < this.conditions.minPurchaseValue) valid = false
    if (this.cart.item_count < this.conditions.minimumQuantity) valid = false
    return valid
  }

  subscribe(listener) {
    this.listeners.push(listener)
  }

  async fetchCart() {
    try {
      const response = await fetch(window.theme.routes.cart, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      })
      const cart = await response.json()
      this.updateCart(cart, false)
    } catch (error) {
      console.error('Error fetching cart:', error)
    }
  }

  async setDiscountStatus(status) {
    try {
      const response = await fetch(window.theme.routes.cartUpdate, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { dtails_discount_activated: status } }),
      })
      const cart = await response.json()
      this.isJustSwitched = true
      this.updateCart(cart, status)
    } catch (error) {
      console.error('Error updating discount status:', error)
    }
  }

  cartUpdated() {
    const isDiscountActive = this.cart?.attributes?.dtails_discount_activated === true
    if (this.state.enabled !== isDiscountActive) {
      this.updateAllPrices()

      document.dispatchEvent(
        new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true,
        })
      )
    }

    this.state.enabled = isDiscountActive
    this.listeners.forEach((listener) => listener(this.state))
  }

  async updateAllPrices() {
    const sectionsToUpdate = this.getSectionsToUpdate()
    if (!sectionsToUpdate.length) return

    const updatedSections = await this.fetchUpdatedSections(sectionsToUpdate)
    if (updatedSections) {
      this.updatePriceSections(updatedSections)
    }
  }

  async fetchUpdatedSections(sections) {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    sections.forEach((section) => params.append('sections[]', section))
    url.search = params.toString()

    try {
      const response = await fetch(url.toString())
      if (!response.ok) throw new Error(`Failed to fetch sections: ${response.statusText}`)
      return await response.json()
    } catch (error) {
      console.error('Error fetching updated sections:', error)
      return null
    }
  }

  updatePriceSections(updatedSections) {
    Object.entries(updatedSections).forEach(([key, value]) => {
      const currentSection =
        document.querySelector(`[data-section="${key}"]`) || document.getElementById(`shopify-section-${key}`)
      if (!currentSection) return

      const newSection = this.findNewSection(value, key)
      if (!newSection) return

      this.replacePriceElements(currentSection, newSection)
      this.replaceVariantPickers(currentSection, newSection)
    })
  }

  findNewSection(html, key) {
    const template = document.createElement('template')
    template.innerHTML = html.trim()

    return (
      template.content.querySelector(`[data-section="${key}"]`) ||
      template.content.getElementById(`shopify-section-${key}`)
    )
  }

  replacePriceElements(currentSection, newSection) {
    const currentPrices = currentSection.querySelectorAll('[data-price-section]')
    const newPrices = newSection.querySelectorAll('[data-price-section]')

    if (currentPrices.length !== newPrices.length) {
      console.warn(`Mismatch in price section elements count for section: ${currentSection}`)
    }

    currentPrices.forEach((current, index) => {
      if (newPrices[index]) {
        current.innerHTML = newPrices[index].innerHTML
      }
    })
  }

  replaceVariantPickers(currentSection, newSection) {
    const currentVariantPickers = currentSection.querySelectorAll('variant-picker')
    const newVariantPickers = newSection.querySelectorAll('variant-picker')

    if (currentVariantPickers.length !== newVariantPickers.length) {
      console.warn(`Mismatch in variant-picker elements count for section: ${currentSection}`)
    }

    currentVariantPickers.forEach((current, index) => {
      if (newVariantPickers[index]) {
        current.replaceWith(newVariantPickers[index])
      }
    })
  }

  getSectionsToUpdate() {
    return [
      ...new Set(
        [...document.querySelectorAll('[data-price-section]')].map((entry) => entry.getAttribute('data-price-section'))
      ),
    ].filter(Boolean)
  }
}

class DiscountToggle extends HTMLElement {
  constructor() {
    super()

    this.manager = new DiscountCampaignManager()
  }

  connectedCallback() {
    this.input = this.querySelector('.toggle-input')
    if (!this.input) return

    this.input.checked = this.manager.state.enabled

    this.manager.subscribe((state) => {
      this.input.checked = state.enabled
    })

    this.input?.addEventListener('change', (event) => {
      this.manager.setDiscountStatus(event.target.checked)
    })
  }

  setFalse() {
    this.input.checked = false
    this.manager.dialog.showModal()
  }
}

customElements.define('discount-toggle', DiscountToggle)
